import { selectItemById } from '@data/selectors/itemSelectors';
import { Switcher } from '@kvdbil/components';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { formatRegistrationPlate, getVehicleFullName } from '@utils/vehicle';
import {
  Button,
  Flex,
  ImageIcon,
  Select,
  Stack,
  mq,
  theme,
} from '@kvdbil/components';
import { useCallback, useRef } from 'react';
import ItemCard from '../components/ItemCard';
import { UploadSelectOption } from '@shared/types';
import { vehicleTypeChipSwitch } from '../components/VehicleTypeChipSwitch';
import DefaultSpinner from '@components/ui/DefaultSpinner';

const Container = styled.div`
  overflow: initial;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const StyledLabel = styled(Flex)`
  font-weight: 600;
`;

const StyledSelect = styled(Select)`
  width: 9.5625rem;

  singleValue {
    width: 300rem;
  }
`;

const UploadCard = styled(Flex)`
  padding: 1.5rem 1rem;

  background-color: ${theme.colors.background.light};
`;

const UploadContent = styled(Stack)`
  ${mq('tablet')} {
    max-width: 40rem;
  }
`;

const StyledButton = styled(Button)`
  justify-content: center;
`;

type UploadViewProps = {
  onFileSelected: (files: FileList | null) => void;
  onChangeMediaType: (type: UploadSelectOption) => void;
  selectedMediaTypeOption: UploadSelectOption;
  visible: boolean;
  changeVisibility: (visible: boolean) => void;
};

const ItemView = ({
  onFileSelected,
  onChangeMediaType,
  selectedMediaTypeOption,
  visible,
  changeVisibility,
}: UploadViewProps) => {
  const { itemId } = useParams<{
    itemId: string;
  }>();

  const { t } = useTranslation();

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const item = useSelector(selectItemById(itemId));

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;

      if (!files) {
        return;
      }

      onFileSelected(files);
    },
    [onFileSelected]
  );

  const handleButtonClick = () => {
    fileInputRef?.current?.click();
  };

  if (!item) {
    return (
      <Flex grow={1}>
        <DefaultSpinner />
      </Flex>
    );
  }

  const formattedRegNr = formatRegistrationPlate(
    item?.properties?.registrationPlate ?? ''
  );

  const subTitle = getVehicleFullName(item);
  const title = `${item?.properties?.registrationPlate ? formattedRegNr + ' | ' : ''}${item.id}`;
  const chipsData = [vehicleTypeChipSwitch(t, item.vehicleType)];

  return (
    <Container>
      <Container>
        <ItemCard
          itemId={item.id}
          title={title}
          subTitle={subTitle}
          chips={chipsData}
        />

        <UploadCard grow={1}>
          <UploadContent grow={1}>
            <StyledButton
              onClick={handleButtonClick}
              color="info"
              prefixIcon={<ImageIcon />}
            >
              {t('Upload images')}
            </StyledButton>
            <input
              type="file"
              style={{ display: 'none' }}
              ref={fileInputRef}
              onChange={handleFileChange}
              accept="image/*"
              multiple
              data-testid="file-input"
            />

            <Flex grow={1} align={'center'} justify={'space-between'}>
              <StyledLabel>{t('Upload to')}</StyledLabel>

              <StyledSelect
                options={[
                  { value: 'PRESENTATION', label: t('Presentation') },
                  { value: 'DEVIATION', label: t('Deviation') },
                  { value: 'RECEPTION', label: t('Reception') },
                ]}
                placeholder={t('Select')}
                onChange={onChangeMediaType}
                value={selectedMediaTypeOption}
                name={'mediaType'}
                isSearchable={false}
                isClearable={false}
                color="info"
              />
            </Flex>
            <Flex grow={1} align={'center'} justify={'space-between'}>
              <StyledLabel>{t('Images are visible')}</StyledLabel>
              <Switcher
                selected={visible}
                onToggle={changeVisibility}
                data-testid={'visibility-switch'}
              />
            </Flex>
          </UploadContent>
        </UploadCard>
      </Container>
    </Container>
  );
};

export default ItemView;
